import { useLingui } from "@lingui/react/macro";
import type { Bot } from "@rakazo/contracts";
import { MOBILE_BOT_RAIL_WIDTH } from "@rakazo/core";
import { BotAvatar } from "@rakazo/ui-web";
import { Menu } from "lucide-react";

export type MobileRailBot = Pick<
  Bot,
  "id" | "name" | "color" | "status" | "unread" | "coordinatorOnly"
> & {
  spaceId: string;
};

export function MobileBotRail({
  bots,
  activeBotId,
  onSelect,
  onOpenNavigation,
  covered = false,
}: {
  bots: MobileRailBot[];
  activeBotId?: string;
  onSelect: (bot: MobileRailBot) => void;
  onOpenNavigation: () => void;
  covered?: boolean;
}) {
  const { t } = useLingui();
  return (
    <nav
      data-testid="mobile-bot-rail"
      aria-label={t`Bots`}
      aria-hidden={covered || undefined}
      inert={covered || undefined}
      className="flex h-full shrink-0 flex-col border-e border-sidebar-border bg-sidebar pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pt-[max(env(safe-area-inset-top),0.5rem)] md:hidden"
      style={{ width: `calc(${MOBILE_BOT_RAIL_WIDTH}px + env(safe-area-inset-left))` }}
    >
      <button
        type="button"
        aria-label={t`Open navigation`}
        onClick={onOpenNavigation}
        className="mx-auto mb-2 grid size-11 shrink-0 place-items-center rounded-2xl text-muted-foreground hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Menu size={19} />
      </button>
      <div className="rk-scroll min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-1.5 pb-3">
        <div className="flex flex-col gap-2">
          {bots.map((bot) => (
            <button
              key={bot.id}
              type="button"
              aria-label={bot.unread ? t`${bot.name}, unread` : bot.name}
              title={bot.name}
              aria-current={bot.id === activeBotId ? "page" : undefined}
              onClick={() => onSelect(bot)}
              className={`relative grid size-11 shrink-0 place-items-center rounded-2xl outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring ${bot.id === activeBotId ? "bg-sidebar-accent ring-1 ring-border" : ""}`}
            >
              <BotAvatar
                identity={bot.id}
                color={bot.color}
                status={bot.status}
                size={32}
                coordinatorLabel={bot.coordinatorOnly ? t`Coordinator` : undefined}
              />
              {bot.unread ? (
                <span
                  aria-hidden="true"
                  className="absolute end-1 top-1 size-2 rounded-full bg-unread ring-2 ring-sidebar"
                />
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}
